import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  Pressable,
  FlatList,
  ActivityIndicator,
  Alert,
  StyleSheet,
} from "react-native";
import { auth, db } from "@/lib/firebase";
import NamePromptModal from "@/components/admin/NamePromptModal";
import {
  ModerationStatus,
  adminListCategories,
  adminCreateApprovedCategory,
  adminCategoryNameExists,
  adminRenameCategory,
  adminApproveCategory,
  adminRejectCategory,
  adminSoftDeleteCategory,
  adminRestoreCategory,
} from "@/lib/taxonomyAdmin";

type Cat = any;

const STATUS_TABS: ModerationStatus[] = ["approved", "pending", "rejected"];

export default function AdminCategoriesManager() {
  const adminUid = auth.currentUser?.uid;

  const [status, setStatus] = useState<ModerationStatus>("approved");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Cat[]>([]);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editing, setEditing] = useState<Cat | null>(null);

  async function load() {
    if (!adminUid) return;
    setLoading(true);
    try {
      const data = await adminListCategories(db, { status, includeDeleted });
      setRows(data);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load categories.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid, status, includeDeleted]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r?.name ?? "").toLowerCase().includes(q));
  }, [rows, search]);

  function openAdd() {
    setModalMode("add");
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(cat: Cat) {
    setModalMode("edit");
    setEditing(cat);
    setModalOpen(true);
  }

  async function handleSaveName(name: string) {
    if (!adminUid) return;

    try {
      if (modalMode === "add") {
        const exists = await adminCategoryNameExists(db, name);
        if (exists) {
          Alert.alert("Already exists", `"${name}" is already in categories (approved or pending).`);
          return;
        }
        await adminCreateApprovedCategory(db, { name, adminUid });
      } else if (editing?.id) {
        await adminRenameCategory(db, { categoryId: editing.id, name, adminUid });
      }

      setModalOpen(false);
      await load();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to save.");
    }
  }

  async function approve(cat: Cat) {
    if (!adminUid) return;
    try {
      await adminApproveCategory(db, { categoryId: cat.id, adminUid });
      await load();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Approve failed.");
    }
  }

  async function reject(cat: Cat) {
    if (!adminUid) return;
    Alert.alert("Reject category?", `Reject "${cat.name}"?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          try {
            await adminRejectCategory(db, { categoryId: cat.id, adminUid });
            await load();
          } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Reject failed.");
          }
        },
      },
    ]);
  }

  async function toggleDelete(cat: Cat) {
    if (!adminUid) return;

    if (cat.isDeleted) {
      try {
        await adminRestoreCategory(db, { categoryId: cat.id, adminUid });
        await load();
      } catch (e: any) {
        Alert.alert("Error", e?.message ?? "Restore failed.");
      }
      return;
    }

    Alert.alert("Delete category?", `Soft delete "${cat.name}"?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          try {
            await adminSoftDeleteCategory(db, { categoryId: cat.id, adminUid });
            await load();
          } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Delete failed.");
          }
        },
      },
    ]);
  }

  return (
    <View style={{ flex: 1 }}>
      <View style={styles.row}>
        {STATUS_TABS.map((s) => (
          <Pressable
            key={s}
            onPress={() => setStatus(s)}
            style={[styles.pill, status === s && styles.pillActive]}
          >
            <Text style={[styles.pillText, status === s && styles.pillTextActive]}>
              {s.toUpperCase()}
            </Text>
          </Pressable>
        ))}
      </View>

      <View style={styles.row}>
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search categories…"
          style={[styles.input, { flex: 1 }]}
        />

        <Pressable onPress={openAdd} style={styles.primaryBtn}>
          <Text style={styles.primaryBtnText}>+ Add</Text>
        </Pressable>
      </View>

      <View style={styles.row}>
        <Pressable
          onPress={() => setIncludeDeleted((v) => !v)}
          style={[styles.toggle, includeDeleted && styles.toggleOn]}
        >
          <Text style={[styles.toggleText, includeDeleted && styles.toggleTextOn]}>
            Include Deleted: {includeDeleted ? "ON" : "OFF"}
          </Text>
        </Pressable>

        <Pressable onPress={load} style={styles.ghostBtn}>
          <Text style={styles.ghostBtnText}>Refresh</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading…</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
          renderItem={({ item }) => (
            <View style={[styles.card, item.isDeleted && { opacity: 0.6 }]}>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>
                  {item.name}{" "}
                  {item.isDeleted ? <Text style={styles.badge}>DELETED</Text> : null}
                </Text>
                <Text style={styles.meta}>
                  status: {item.status} · id: {item.id}
                </Text>
              </View>

              <View style={styles.actions}>
                <Pressable onPress={() => openEdit(item)} style={styles.btn}>
                  <Text style={styles.btnText}>Edit</Text>
                </Pressable>

                {item.status === "pending" ? (
                  <>
                    <Pressable onPress={() => approve(item)} style={[styles.btn, styles.btnGood]}>
                      <Text style={[styles.btnText, styles.btnTextGood]}>Approve</Text>
                    </Pressable>
                    <Pressable onPress={() => reject(item)} style={[styles.btn, styles.btnBad]}>
                      <Text style={[styles.btnText, styles.btnTextBad]}>Reject</Text>
                    </Pressable>
                  </>
                ) : null}

                <Pressable onPress={() => toggleDelete(item)} style={[styles.btn, styles.btnBad]}>
                  <Text style={[styles.btnText, styles.btnTextBad]}>
                    {item.isDeleted ? "Restore" : "Delete"}
                  </Text>
                </Pressable>
              </View>
            </View>
          )}
          ListEmptyComponent={
            <View style={{ padding: 16 }}>
              <Text style={{ opacity: 0.7 }}>No categories.</Text>
            </View>
          }
        />
      )}

      <NamePromptModal
        visible={modalOpen}
        title={modalMode === "add" ? "Add Category (Approved)" : "Edit Category"}
        description={
          modalMode === "add"
            ? "This will be created as APPROVED and visible to users immediately."
            : "Rename the category (keeps same ID)."
        }
        initialValue={modalMode === "edit" ? editing?.name : ""}
        confirmText={modalMode === "add" ? "Create" : "Save"}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleSaveName}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: "row", gap: 10, alignItems: "center", marginBottom: 10 },
  pill: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#fafafa",
  },
  pillActive: { backgroundColor: "#111", borderColor: "#111" },
  pillText: { fontWeight: "900", fontSize: 12 },
  pillTextActive: { color: "white" },

  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  primaryBtn: {
    backgroundColor: "#111",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  primaryBtnText: { color: "white", fontWeight: "900" },

  ghostBtn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  ghostBtnText: { fontWeight: "900" },

  toggle: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#fafafa",
  },
  toggleOn: { backgroundColor: "#111", borderColor: "#111" },
  toggleText: { fontWeight: "900" },
  toggleTextOn: { color: "white" },

  loadingRow: { flexDirection: "row", gap: 8, alignItems: "center", padding: 12 },

  card: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    padding: 12,
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
    backgroundColor: "white",
  },
  cardTitle: { fontSize: 15, fontWeight: "900" },
  badge: { fontSize: 12, fontWeight: "900", opacity: 0.6 },
  meta: { marginTop: 4, opacity: 0.7, fontSize: 12 },

  actions: { gap: 8, alignItems: "flex-end" },
  btn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  btnText: { fontWeight: "900" },
  btnGood: { borderColor: "#2e7d32" },
  btnTextGood: { color: "#2e7d32" },
  btnBad: { borderColor: "#c62828" },
  btnTextBad: { color: "#c62828" },
});
